import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../hooks/NotificationContext';
import {
  addField,
  deleteField,
  getFieldConfigurations,
  updateField,
} from '../../modules/config';
import EditableTable from '../elements/EditableTable';
import AddForm from './AddForm';
import EntityDeleteConfirmation from './EntityDeleteConfirmation';
import { getConfigDetails, getTableHeader } from './tableData';

const subHeaderTabs = [{ key: 'name', label: 'Name', width: '200px' }];

const CustomFieldsComponent = ({ isAdmin, selectedTab }) => {
  const { addNotification } = useNotification();
  const { projectId } = useParams();
  const { group } = getConfigDetails(selectedTab);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFieldConfigurations(projectId);
      setData(response?.data || []);
    } catch (error) {
      addNotification('danger', error.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteClick = (id) => {
    setEntityToDelete(id);
    setShowConfirmationModal(true);
  };

  const handleConfirmDelete = async () => {
    setLoadingDelete(true);
    try {
      await deleteField(entityToDelete);
      fetchData();
      setShowConfirmationModal(false);
    } catch (error) {
      addNotification('danger', error.message);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleAddNewEntity = async (entity, parentRow = null) => {
    try {
      const payload = {
        name: entity.name,
        description: entity.description || null,
        parentId: parentRow?.id || null,
        type: 'PROJECT',
        fieldType: parentRow ? 'SUBCATEGORY' : 'CATEGORY',
        gainssProjectId: projectId,
      };
      await addField(payload);
      fetchData();
    } catch (error) {
      addNotification('danger', error.message);
    }
  };

  const handleUpdateEntity = async (id, updatedData) => {
    try {
      const payload = {
        name: updatedData.name,
        description: updatedData.description || null,
        projectId: projectId,
      };
      await updateField(id, payload);
      fetchData();
    } catch (error) {
      addNotification('danger', error.message);
    }
  };

  return (
    <Box>
      {isAdmin && (
        <Box sx={{ mb: 2 }}>
          <AddForm
            entityType={selectedTab}
            handleSubmit={handleAddNewEntity}
            entity="Category"
          />
        </Box>
      )}

      <EditableTable
          data={data}
          type={selectedTab}
          headerTabs={getTableHeader(selectedTab)}
          hasSubRows
          subHeaderTabs={subHeaderTabs}
          subRowsKey="sub_categories"
          subTableName="Subcategories"
          isAdmin={isAdmin}
          isLoading={loading || loadingDelete}
          editableFields={['name']}
          handleUpdateEntity={handleUpdateEntity}
          handleAddSubEntity={handleAddNewEntity}
          handleUpdateSubEntity={handleUpdateEntity}
          handleDeleteSubEntity={handleConfirmDelete}
          canEdit={(row) => row.gainss_project_id !== null && row.scope !== 'GLOBAL'}
          handleDeleteClick={handleDeleteClick}
          canDelete={(row) => row.scope !== 'GLOBAL'}
        />

      <EntityDeleteConfirmation
          show={showConfirmationModal}
          handleClose={() => setShowConfirmationModal(false)}
          handleConfirm={handleConfirmDelete}
          entityToDelete={entityToDelete}
          type={group}
      />
    </Box>
  );
};

export default CustomFieldsComponent;